(ns client-portal.client.user-management.user.views
  (:require [client-portal.client.user-management.components :as um.c]
            [client-portal.client.user-management.data :as um.d]
            [client-portal.client.user-management.user.data :as user.d]
            [client-portal.client.voice-insights.empty-state :as empty-state]
            [clojure.string :as s]
            [shared.core.amelia-light.components.components :as alc]
            [shared.core.reactive :as re]
            [shared.core.style :as style]
            [shared.core.util :as util]))

(defn connection-label [connection-name]
  (get {"Username-Password-Authentication" "Email and password"
        "google-oauth2" "Google"}
       connection-name
       connection-name))

(defn- self? [email-address]
  (= (some-> email-address (s/lower-case))
     (some-> @(re/subscribe [:auth0/user-]) :email (s/lower-case))))

(defn- concept-label [concepts-by-id concept-id]
  (get-in concepts-by-id [concept-id :label] (str "Concept " concept-id)))

(defn- form-concept-ids
  "The concepts the form's organizations cover, alphabetically"
  [{:keys [organizations]} organizations-by-id concepts-by-id]
  (->> organizations
       (mapcat #(get-in organizations-by-id [(:tenant-organization-id %) :concept-ids]))
       (distinct)
       (sort-by #(s/lower-case (concept-label concepts-by-id %)))))

(defn access-summary
  "What the person can do, in plain words, kept current as the form changes"
  []
  (let [{:keys [active application-ids application-role-ids organizations location-limits] :as form}
        @(re/subscribe [:user-management/user-form-])
        organizations-by-id @(re/subscribe [:user-management/organizations-by-id-])
        applications-by-id @(re/subscribe [:user-management/applications-by-id-])
        roles-by-id @(re/subscribe [:user-management/roles-by-id-])
        concepts-by-id @(re/subscribe [:user-management/concepts-by-id-])
        organization-labels (keep #(get-in organizations-by-id [(:tenant-organization-id %) :label]) organizations)
        concept-ids (form-concept-ids form organizations-by-id concepts-by-id)
        limited-concept-ids (->> location-limits
                                 (map :concept-id)
                                 (filter (set concept-ids))
                                 (distinct))
        application-text (fn [{:keys [id label]}]
                           (let [role-labels (->> application-role-ids
                                                  (keep roles-by-id)
                                                  (filter #(= id (:application-id %)))
                                                  (map :label)
                                                  (sort))]
                             (if (empty? role-labels)
                               (str label " without a role")
                               (str label " as " (um.c/join-words role-labels)))))
        applications (->> application-ids
                          (keep applications-by-id)
                          (sort-by :label))
        concepts-text (fn [ids]
                        (um.c/join-words (map #(concept-label concepts-by-id %) ids)))]
    [:p {:aria-live "polite"
         :style {:color (style/alc-color "Text/secondary")
                 :font-size 14
                 :line-height 1.5
                 :margin 0
                 :max-width "72ch"}}
     (->> (if-not active
            ["Deactivated, so they can't sign in anywhere."]
            [(if (empty? organization-labels)
               "Not in any organization here yet."
               (str "Belongs to " (um.c/join-words organization-labels) "."))
             (if (empty? applications)
               "Can't use any application yet."
               (str "Uses " (um.c/join-words (map application-text applications)) "."))
             (cond
               (empty? concept-ids) nil
               (empty? limited-concept-ids) (str "Sees every location in " (um.c/count-text (count concept-ids) "concept" "concepts") ".")
               (= (count limited-concept-ids) (count concept-ids)) (str "Sees only chosen cohorts and locations in "
                                                                        (concepts-text limited-concept-ids) ".")
               :else (str "Sees every location in " (- (count concept-ids) (count limited-concept-ids)) " of "
                          (um.c/count-text (count concept-ids) "concept" "concepts")
                          ", and only chosen cohorts and locations in " (concepts-text limited-concept-ids) "."))])
          (remove nil?)
          (s/join " "))]))

(defn page-header [{:keys [user-id]}]
  (let [{:keys [email-address]} @(re/subscribe [:user-management/user-form-])]
    [:div {:style {:display :flex
                   :flex-direction :column
                   :gap 8
                   :max-width um.c/max-content-width
                   :padding "4px 0 16px"}}
     [alc/up-ghost-button {:icon-type :ChevronLeftIcon
                           :style {:align-self :flex-start
                                   :height 32
                                   :margin-left -8}
                           :on-click #(re/dispatch [:user-management/go :user-management {:section "users"}])}
      "Users"]
     [:h1 {:style {:color (style/alc-color "Text/primary_header")
                   :font-family :inherit
                   :font-size 24
                   :font-weight 400
                   :line-height 1.33
                   :margin 0
                   :overflow-wrap :anywhere}}
      (if user-id email-address "Add User")]
     [access-summary]]))

(defn email-row [_]
  (let [blurred?* (re/ratom false)]
    (fn [_]
      (let [{:keys [email-address]} @(re/subscribe [:user-management/user-form-])
            problem (:email-address @(re/subscribe [:user-management/user-problems-]))]
        [um.c/field-row {:label "Email"
                         :description "If they don't have an account yet, they'll get an email invitation for each organization."}
         [:div {:style {:max-width 420}}
          [alc/up-input {:form-path user.d/form-path
                         :form-name :email-address
                         :type "email"
                         :auto-focus true
                         :auto-complete "off"
                         :aria-label "Email"
                         :placeholder "name@company.com"
                         :on-blur #(reset! blurred?* true)}]]
         (cond
           (map? problem)
           [:div {:style {:align-items :center
                          :display :flex
                          :flex-wrap :wrap
                          :gap 8}}
            [um.c/error-text (:message problem)]
            [alc/up-button {:style {:height 32}
                            :on-click #(re/dispatch [:user-management/go :user-management/user {:user-id (:user-id problem)}])}
             "Open Their Profile"]]

           (and problem @blurred?* (not (s/blank? email-address)))
           [um.c/error-text problem])]))))

(defn status-row []
  (let [{:keys [active email-address]} @(re/subscribe [:user-management/user-form-])
        self? (self? email-address)
        manager? @(re/subscribe [:user-management/manager?-])]
    [um.c/field-row {:label "Active"
                     :description "Inactive people can't sign in to any application, in any organization."}
     [:div {:style {:align-items :center
                    :display :flex
                    :gap 12
                    :min-height 36}}
      [alc/up-toggle {:checked (boolean active)
                      :disabled (or self? manager?)
                      :aria-label "Active"
                      :on-checked-change #(re/dispatch [:user-management/set-user-field :active %])}]
      [:span {:style {:color (style/alc-color "Text/primary")
                      :font-size 14}}
       (if active "Active" "Inactive")]]
     (cond
       self? [um.c/secondary-text {} "You can't deactivate your own account."]
       manager? [um.c/secondary-text {} "Only an admin can deactivate or reactivate someone."])]))

(defn- days-until [iso]
  (some-> iso (js/Date.) (.getTime) (- (.now js/Date)) (/ 86400000) (js/Math.ceil)))

(defmethod alc/dialog-view
  :user-management/revoke-invitation [_ {:keys [user-id tenant-organization-id email-address organization-label]}]
  [um.c/confirm-dialog {:title "Revoke Invitation"
                        :message (str "The link in " email-address "'s invitation to " organization-label
                                      " will stop working. They'll stay in the organization here. To invite them again, "
                                      "choose how they'll sign in and save.")
                        :confirm-text "Revoke Invitation"
                        :busy? @(re/subscribe [:user-management/invitation-request-active?-])
                        :on-confirm #(do (re/dispatch [:dialog/hide])
                                         (re/dispatch [:user-management/revoke-invitation {:user-id user-id
                                                                                           :tenant-organization-id tenant-organization-id}]))}])

(defn invitation-status [{:keys [user-id email-address tenant-organization-id organization-label]}]
  (let [invitations (->> (get @(re/subscribe [:user-management/invitations-by-email-]) (s/lower-case email-address))
                         (filter #(= tenant-organization-id (:tenant-organization-id %))))
        pending (first (filter :active invitations))
        busy? @(re/subscribe [:user-management/invitation-request-active?-])
        can-resend? @(re/subscribe [:account/has-permission?- :create:users])
        can-revoke? @(re/subscribe [:account/has-permission?- :delete:users])
        action-style {:height 32}]
    (when (not-empty invitations)
      [:div {:style {:align-items :center
                     :border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))
                     :display :flex
                     :flex-wrap :wrap
                     :gap 4
                     :padding-top 10}}
       [:div {:style {:display :flex
                      :flex "1 1 220px"
                      :flex-direction :column
                      :gap 2}}
        [:span {:style {:color (style/alc-color "Text/primary")
                        :font-size 13}}
         [um.c/status-badge (if pending :invited :expired)]]
        [um.c/secondary-text {}
         (if pending
           (let [days (days-until (:expires-at pending))]
             (str "They haven't accepted yet. The link expires " (if (<= days 1) "within a day" (str "in " days " days")) "."))
           "It expired before they accepted. Resend it to give them a new link.")]]
       (when pending
         [alc/up-ghost-button {:icon-type :CopyIcon
                               :style action-style
                               :on-click #(re/dispatch-sync [:core/copy-to-clipboard (:invitation-url pending) {:notify? true}])}
          "Copy Link"])
       (when can-resend?
         [alc/up-ghost-button {:icon-type :SendIcon
                               :style action-style
                               :disabled busy?
                               :on-click #(re/dispatch [:user-management/resend-invitation {:user-id user-id
                                                                                            :tenant-organization-id tenant-organization-id}])}
          "Resend"])
       (when can-revoke?
         [alc/up-ghost-button {:icon-type :DeleteIcon
                               :style action-style
                               :disabled busy?
                               :on-click #(re/dispatch [:dialog/show [:user-management/revoke-invitation
                                                                      {:user-id user-id
                                                                       :tenant-organization-id tenant-organization-id
                                                                       :email-address email-address
                                                                       :organization-label organization-label}]])}
          "Revoke"])])))

(defn organization-card [{:keys [key tenant-organization-id] :as row}]
  (let [{:keys [user-id email-address original]} @(re/subscribe [:user-management/user-form-])
        organization (get @(re/subscribe [:user-management/organizations-by-id-]) tenant-organization-id)
        available @(re/subscribe [:user-management/organization-connections- tenant-organization-id])
        ;; An existing membership shows the sign-in methods Auth0 has for it
        user-connections (when (and user-id (some #(= tenant-organization-id (:tenant-organization-id %)) (:organizations original)))
                           @(re/subscribe [:user-management/user-connections- user-id]))
        connections @(re/subscribe [:user-management/row-connections- row])
        loading? (or (util/nil-or-loading? available) (util/loading? user-connections))
        label-id (str "um-sign-in-" key)]
    [:div {:style {:background-color (style/alc-color "Background/secondary")
                   :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                   :border-radius 8
                   :display :flex
                   :flex-direction :column
                   :gap 10
                   :padding 12}}
     [:div {:style {:align-items :center
                    :display :flex
                    :gap 8}}
      [:span {:style {:color (style/alc-color "Text/primary_header")
                      :display :flex
                      :flex 1
                      :font-size 14
                      :font-weight 500
                      :min-width 0}}
       [um.c/organization-name (or organization {:label "Unknown organization"})]]
      [alc/up-ghost-button {:icon-type :DeleteIcon
                            :icon "sm"
                            :aria-label (str "Remove from " (:label organization))
                            :tooltip-content "Remove From Organization"
                            :on-click #(re/dispatch [:user-management/remove-user-organization key])}]]
     [:div {:style {:display :flex
                    :flex-direction :column
                    :gap 6}}
      [:span {:id label-id
              :style {:color (style/alc-color "Text/secondary")
                      :font-size 13}}
       "Signs in with"]
      (if (or (= available :error) (= user-connections :error))
        [um.c/error-text "Couldn't load sign-in methods from Auth0. Reload to try again."]
        [alc/up-multiple-dropdown {:value (vec connections)
                                   :aria-labelledby label-id
                                   :disabled loading?
                                   :placeholder (cond
                                                  loading? "Loading sign-in methods…"
                                                  user-connections "No account yet"
                                                  :else "Choose how they'll sign in")
                                   :options (->> (concat (map :name (um.d/rows available)) connections)
                                                 (distinct)
                                                 (mapv #(hash-map :text (connection-label %)
                                                                  :value %)))
                                   :on-change #(re/dispatch [:user-management/set-user-organization-connections key %])}])]
     (when user-id
       [invitation-status {:user-id user-id
                           :email-address email-address
                           :tenant-organization-id tenant-organization-id
                           :organization-label (:label organization)}])]))

(defn organizations-row []
  (let [{:keys [user-id organizations]} @(re/subscribe [:user-management/user-form-])
        tenant-organizations (um.d/rows @(re/subscribe [:user-management/organizations-]))
        chosen (set (map :tenant-organization-id organizations))
        addable (remove #(contains? chosen (:tenant-organization-id %)) tenant-organizations)]
    [um.c/field-row {:label "Organizations"
                     :description "Where they belong and how they sign in to each. Members see the concepts their organizations cover."}
     (when (not-empty organizations)
       (into [:div {:style {:display :flex
                            :flex-direction :column
                            :gap 8}}]
             (for [row organizations]
               ^{:key (:key row)}
               [organization-card row])))
     (when (and user-id (empty? organizations))
       [um.c/secondary-text {} "Saving with no organizations removes them from everything in this tenant."])
     (when (not-empty addable)
       [:div {:style {:align-self :flex-start}}
        [alc/up-popover-button-dropdown {:placeholder "Add Organization"
                                         :fixed-icon-type :AddIcon
                                         :search? (< 6 (count addable))
                                         :search-placeholder "Search organizations"
                                         :no-results-message "No organizations found"
                                         :align :start
                                         :options (mapv (fn [{:keys [tenant-organization-id label]}]
                                                          {:text label
                                                           :value tenant-organization-id})
                                                        addable)
                                         :on-change #(re/dispatch [:user-management/add-user-organization %])}]])]))

(defn applications-row []
  (let [{:keys [application-ids application-role-ids email-address original]} @(re/subscribe [:user-management/user-form-])
        applications (um.d/rows @(re/subscribe [:user-management/applications-]))
        roles (um.d/rows @(re/subscribe [:user-management/roles-]))
        client-portal-id (some #(when (= "client-portal" (:external-id %)) (:id %)) applications)
        admin-role-id (some #(when (and (= client-portal-id (:application-id %)) (= "admin" (:label %))) (:id %)) roles)
        ;; Internal-only roles aren't offered to someone outside SoundHound, and a manager is only offered the roles they
        ;; have, though a role the person already holds stays visible
        external? (and (not (s/blank? email-address)) (not (util/soundhound-email? email-address)))
        grantable-role-ids @(re/subscribe [:user-management/grantable-role-ids-])
        offered? #(or (contains? application-role-ids (:id %))
                      (and (or (not (:internal %)) (not external?))
                           (or (nil? grantable-role-ids) (contains? grantable-role-ids (:id %)))))
        problem (:roles @(re/subscribe [:user-management/user-problems-]))]
    [um.c/field-row {:label "Applications and roles"
                     :description "Access lets them sign in to an application. Roles decide what they can do once they're in."}
     (if (or (nil? applications) (nil? roles))
       [um.c/secondary-text {} "Loading applications…"]
       (into [:div {:style {:display :flex
                            :flex-direction :column
                            :gap 12}}]
             (for [{:keys [id label]} (sort-by (juxt #(not= "client-portal" (:external-id %)) :label) applications)
                   :let [access? (contains? application-ids id)
                         application-roles (sort-by :label (filter #(= id (:application-id %)) roles))
                         application-role-id? (set (map :id application-roles))]]
               ^{:key id}
               [:div {:class-name "um-application-row"}
                [alc/up-toggle {:checked access?
                                :aria-label (str "Access to " label)
                                :on-checked-change #(re/dispatch [:user-management/set-application-access id %])}]
                [:span {:style {:color (style/alc-color (if access? "Text/primary" "Text/secondary"))
                                :font-size 14}}
                 label]
                [:div {:class-name "um-roles"}
                 [alc/up-multiple-dropdown {:value (vec (filter application-role-id? application-role-ids))
                                            :aria-label (str label " roles")
                                            :placeholder (if (empty? application-roles) "This application has no roles" "Select roles")
                                            :disabled (empty? application-roles)
                                            :options (->> application-roles
                                                          (filter offered?)
                                                          (mapv (fn [{:keys [id label internal]}]
                                                                  {:text (if internal (str label " (Internal Only)") label)
                                                                   :value id})))
                                            :on-change #(re/dispatch [:user-management/set-application-roles id (set %)])}]]])))
     (when problem
       [um.c/error-text problem])
     (when (and (self? email-address)
                admin-role-id
                (contains? (:application-role-ids original) admin-role-id)
                (not (contains? application-role-ids admin-role-id)))
       [um.c/error-text "Saving takes away your own admin role, and with it your access to User Management."])]))

(defn location-limit-picker [_]
  (let [open?* (re/ratom false)
        search* (re/ratom "")]
    (fn [{:keys [concept-id concept-name]}]
      (let [{:keys [location-limits]} @(re/subscribe [:user-management/user-form-])
            added? (set (map user.d/limit-key location-limits))
            options (when @open?*
                      @(re/subscribe [:user-management/location-access-options- concept-id]))
            search (some-> @search* (s/trim) (s/lower-case) (not-empty))
            matches? #(or (nil? search)
                          (s/includes? (s/lower-case (str (:label %))) search)
                          (s/starts-with? (str (:id %)) search))
            cohorts (filterv matches? (:cohorts options))
            locations (filterv matches? (:locations options))
            shown-locations (take 50 locations)
            option-button (fn [{:keys [limit text detail]}]
                            (let [added? (added? (user.d/limit-key limit))]
                              [:button {:type "button"
                                        :class-name um.c/option-class-name
                                        :disabled added?
                                        :style {:color (style/alc-color "Text/primary")
                                                :cursor :pointer
                                                :font-size 14
                                                :padding "6px 8px"}
                                        :on-click #(re/dispatch [:user-management/add-location-limit limit])}
                               [:span {:style {:display :flex
                                               :flex 1
                                               :flex-direction :column
                                               :min-width 0}}
                                [:span {:style {:overflow :hidden
                                                :text-overflow :ellipsis
                                                :white-space :nowrap}}
                                 text]
                                (when detail
                                  [um.c/secondary-text {:style {:font-size 12}} detail])]
                               (when added?
                                 [alc/up-icon {:icon-type :DoneIcon
                                               :class-name "h-4 w-4"}])]))
            group-label (fn [text]
                          [:div {:style {:color (style/alc-color "Text/secondary")
                                         :font-size 12
                                         :font-weight 500
                                         :padding "8px 8px 4px"}}
                           text])]
        [alc/up-popover
         {:open @open?*
          :on-open-change #(do (reset! open?* %)
                               (reset! search* ""))
          :align :start
          :class-name "w-max p-0"
          :trigger [:button {:type "button"
                             :class-name um.c/popover-trigger-class-name
                             :style {:cursor :pointer}}
                    [alc/up-secondary-icon {:icon-type :AddIcon}]
                    "Add Cohort or Location"]
          :content [:div {:style {:display :flex
                                  :flex-direction :column
                                  :max-width "calc(100vw - 32px)"
                                  :width 340}}
                    [:div {:style {:border-bottom (str "1px solid " (style/alc-color "Border/divider_secondary"))
                                   :padding 8}}
                     [alc/up-input {:auto-focus true
                                    :default-value ""
                                    :aria-label (str "Search " concept-name " cohorts and locations")
                                    :placeholder "Search by name or ID"
                                    :on-change #(reset! search* (.. % -target -value))}]]
                    [:div {:style {:max-height 340
                                   :overflow-y :auto
                                   :padding 4}}
                     (cond
                       (= options :error)
                       [:div {:style {:padding 12}}
                        [um.c/error-text "Couldn't load this concept's cohorts and locations."]]

                       (util/nil-or-loading? options)
                       [:div {:style {:padding 12}}
                        [um.c/secondary-text {} "Loading cohorts and locations…"]]

                       (and (empty? cohorts) (empty? locations))
                       [:div {:style {:padding 12}}
                        [um.c/secondary-text {} (if search
                                                  "Nothing matches. Try part of a name or an ID instead."
                                                  "This concept has no cohorts or locations.")]]

                       :else
                       [:<>
                        (when (not-empty cohorts)
                          [group-label "Cohorts"])
                        (for [{:keys [id label cohort-type-label]} cohorts]
                          ^{:key (str "cohort-" id)}
                          [option-button {:text label
                                          :detail (when (and cohort-type-label (not= cohort-type-label label))
                                                    cohort-type-label)
                                          :limit {:kind "cohort"
                                                  :concept-id concept-id
                                                  :cohort-id id
                                                  :label label
                                                  :cohort-type-label cohort-type-label}}])
                        (when (not-empty locations)
                          [group-label "Locations"])
                        (for [{:keys [id label]} shown-locations]
                          ^{:key (str "location-" id)}
                          [option-button {:text label
                                          :limit {:kind "location"
                                                  :concept-id concept-id
                                                  :location-id id
                                                  :label label}}])
                        (when (< (count shown-locations) (count locations))
                          [:div {:style {:padding "8px 8px 4px"}}
                           [um.c/secondary-text {}
                            (str "Showing 50 of " (.toLocaleString (count locations)) " locations. Keep typing to narrow the list.")]])])]]}]))))

(defn limited-concept [{:keys [concept-id concept-name limits on-collapse]}]
  [:div {:style {:background-color (style/alc-color "Background/secondary")
                 :border (str "1px solid " (style/alc-color "Border/divider_secondary"))
                 :border-radius 8
                 :display :flex
                 :flex-direction :column
                 :gap 8
                 :padding 12}}
   [:div {:style {:align-items :center
                  :display :flex
                  :gap 8}}
    [um.c/concept-mark concept-id]
    [:span {:style {:color (style/alc-color "Text/primary_header")
                    :flex 1
                    :font-size 14
                    :font-weight 500
                    :min-width 0}}
     concept-name]
    [alc/up-ghost-button {:icon-type :CrossIcon
                          :icon "sm"
                          :aria-label (str "Stop limiting " concept-name)
                          :tooltip-content (if (empty? limits)
                                             "Don't Limit This Concept"
                                             "Remove Limits for This Concept")
                          :on-click on-collapse}]]
   (if (empty? limits)
     [um.c/secondary-text {} "Every location, until you add a cohort or location here."]
     (into [:div {:style {:display :flex
                          :flex-wrap :wrap
                          :gap 6}}]
           (for [{:keys [kind label] :as limit} limits]
             ^{:key (pr-str (user.d/limit-key limit))}
             [alc/up-tag {:text (str (if (= kind "cohort") "Cohort: " "") label)
                          :title label
                          :style {:max-width "100%"}
                          :on-close #(re/dispatch [:user-management/remove-location-limit limit])}])))
   [:div {:style {:align-self :flex-start}}
    [location-limit-picker {:concept-id concept-id
                            :concept-name concept-name}]]])

(defn location-access-row [_]
  (let [expanded* (re/ratom #{})]
    (fn [_]
      (let [{:keys [location-limits] :as form} @(re/subscribe [:user-management/user-form-])
            organizations-by-id @(re/subscribe [:user-management/organizations-by-id-])
            concepts-by-id @(re/subscribe [:user-management/concepts-by-id-])
            concept-ids (form-concept-ids form organizations-by-id concepts-by-id)
            limits-by-concept (group-by :concept-id location-limits)
            shown (filter #(or (contains? limits-by-concept %) (contains? @expanded* %)) concept-ids)
            unlimited (remove (set shown) concept-ids)]
        [um.c/field-row {:label "Location access"
                         :description (str "They see every location in their organizations' concepts. Limit a concept to "
                                           "specific cohorts or locations to narrow what they see there.")}
         (if (empty? concept-ids)
           [um.c/secondary-text {} "Add an organization with concepts to set location limits."]
           [:<>
            (into [:div {:style {:display :flex
                                 :flex-direction :column
                                 :gap 8}}]
                  (for [concept-id shown
                        :let [concept-name (concept-label concepts-by-id concept-id)
                              limits (get limits-by-concept concept-id)]]
                    ^{:key concept-id}
                    [limited-concept {:concept-id concept-id
                                      :concept-name concept-name
                                      :limits limits
                                      :on-collapse (fn []
                                                     (swap! expanded* disj concept-id)
                                                     (re/dispatch [:user-management/remove-concept-limits concept-id]))}]))
            (if (empty? unlimited)
              (when (not-empty shown)
                [um.c/secondary-text {} "Every concept they can see is limited."])
              [:div {:style {:align-items :center
                             :display :flex
                             :flex-wrap :wrap
                             :gap 8}}
               [alc/up-popover-button-dropdown {:placeholder "Limit a Concept"
                                                :fixed-icon-type :LayersIcon
                                                :search? (< 6 (count unlimited))
                                                :search-placeholder "Search concepts"
                                                :no-results-message "No concepts found"
                                                :align :start
                                                :options (mapv (fn [id]
                                                                 {:text (concept-label concepts-by-id id)
                                                                  :value id})
                                                               unlimited)
                                                :on-change #(swap! expanded* conj %)}]
               [um.c/secondary-text {}
                (str (um.c/count-text (count unlimited) "concept shows" "concepts show") " every location.")]])])]))))

(defn save-bar [{:keys [user-id]}]
  (let [unsaved-count @(re/subscribe [:user-management/user-unsaved-count-])
        problems @(re/subscribe [:user-management/user-problems-])
        problem (some (fn [k]
                        (let [p (get problems k)]
                          (if (map? p) (:message p) p)))
                      [:email-address :organizations :connections :roles])
        saving? @(re/subscribe [:user-management/saving-user?-])]
    (when (or (nil? user-id) (pos? unsaved-count))
      [:div {:class-name "um-save-bar"
             :style {:align-items :center
                     :background-color (style/alc-color "Background/card_hex")
                     :border-top (str "1px solid " (style/alc-color "Border/divider"))
                     :bottom 0
                     :display :flex
                     :flex-wrap :wrap
                     :gap 8
                     :justify-content :flex-end
                     :margin-top :auto
                     :max-width um.c/max-content-width
                     :padding "12px 0"
                     :position :sticky
                     :z-index (style/z :low)}}
       [:span {:role "status"
               :style {:color (style/alc-color "Text/secondary")
                       :flex "1 1 200px"
                       :font-size 13
                       :line-height 1.4}}
        (cond
          problem problem
          (nil? user-id) "Ready to add them."
          :else (um.c/count-text unsaved-count "unsaved change" "unsaved changes"))]
       (if user-id
         [alc/up-ghost-button {:on-click #(re/dispatch [:user-management/discard-user-changes])}
          "Discard"]
         [alc/up-ghost-button {:on-click #(re/dispatch [:user-management/go :user-management {:section "users"}])}
          "Cancel"])
       [alc/up-call-to-action-button {:loading? saving?
                                      :disabled (boolean problem)
                                      :on-click #(re/dispatch [:user-management/save-user])}
        (if user-id "Save Changes" "Add User")]])))

(defn editor [{:keys [user-id]}]
  (let [form-id (util/random-uuid-string)]
    (re/create-class
      {:component-did-mount #(re/dispatch [:user-management/init-user-form form-id user-id])
       :reagent-render
       (fn [{:keys [user-id]}]
         (when (= form-id (:form-id @(re/subscribe [:user-management/user-form-])))
           [:div {:style {:display :flex
                          :flex 1
                          :flex-direction :column}}
            [page-header {:user-id user-id}]
            [:div {:style {:border-top (str "1px solid " (style/alc-color "Border/divider_secondary"))
                           :max-width um.c/max-content-width}}
             (if user-id
               [status-row]
               [email-row])
             [organizations-row]
             [applications-row]
             [location-access-row]]
            [save-bar {:user-id user-id}]]))})))

(defn root [{:keys [user-id]}]
  (let [new? (= "new" user-id)
        user-id (when-not new?
                  (not-empty user-id))
        user (when user-id
               @(re/subscribe [:user-management/user- user-id]))
        organizations @(re/subscribe [:user-management/organizations-])]
    ;; Everything the editor reads, requested up front so it arrives together
    @(re/subscribe [:user-management/applications-])
    @(re/subscribe [:user-management/roles-])
    @(re/subscribe [:user-management/concepts-])
    @(re/subscribe [:user-management/users-])
    (when user-id
      @(re/subscribe [:user-management/invitations-])
      @(re/subscribe [:user-management/user-connections- user-id]))
    [:div {:style {:display :flex
                   :flex 1
                   :flex-direction :column
                   :padding "12px 16px 0"}}
     (cond
       (and new? (not @(re/subscribe [:account/has-permission?- :create:users])))
       [empty-state/no-data-card {:text "You don't have permission to add people. Ask an admin for access."
                                  :material-icon-type :lock}]

       (and new? (util/nil-or-loading? organizations))
       [empty-state/loading-card {:label "Loading organizations…"}]

       new?
       ^{:key "new"} [editor {:user-id nil}]

       (or (nil? user-id) (= user :error))
       [empty-state/no-data-card {:text "This person isn't in any organization here, or couldn't be loaded."
                                  :material-icon-type :person-off}]

       (util/nil-or-loading? user)
       [empty-state/loading-card {:label "Loading their access…"}]

       :else
       ^{:key user-id} [editor {:user-id user-id}])]))
